'use client';

import React from 'react';
import { Calendar, Swords, Trophy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type {
  BracketNode,
  TournamentFormat,
  TournamentMatch,
  TournamentTeam,
} from '../interfaces/tournaments';
import { MATCH_STATUS_LABELS } from '../interfaces/tournaments';
import { BracketPanCanvas } from './BracketPanCanvas';

const MATCH_SLOT_HEIGHT = 96;

interface BracketViewProps {
  rounds: BracketNode[][];
  onMatchClick?: (match: TournamentMatch) => void;
  editable?: boolean;
  format?: TournamentFormat;
  compact?: boolean;
  /** When true, renders bracket content without its own pan canvas (for DoubleBracketView). */
  embedded?: boolean;
  panCanvasClassName?: string;
}

export const BracketView: React.FC<BracketViewProps> = ({
  rounds,
  onMatchClick,
  editable = false,
  format,
  compact = false,
  embedded = false,
  panCanvasClassName,
}) => {
  if (!rounds || rounds.length === 0) {
    return <BracketEmptyState />;
  }

  const totalRounds = rounds.length;
  const totalMatches = rounds.reduce((sum, r) => sum + r.length, 0);
  const completedMatches = rounds.reduce(
    (sum, r) => sum + r.filter((n) => n.match.status === 'completed').length,
    0
  );

  const isElimination =
    !format || format === 'single_elimination' || format === 'double_elimination';

  if (format === 'round_robin' && rounds.length === 1) {
    return (
      <div className="space-y-4 animate-in fade-in duration-300">
        {!compact && (
          <BracketProgressBar
            completed={rounds[0].filter((n) => n.match.status === 'completed').length}
            total={rounds[0].length}
          />
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {rounds[0].map((node) => (
            <MatchCard
              key={node.id}
              node={node}
              onClick={
                editable && onMatchClick ? () => onMatchClick(node.match) : undefined
              }
              editable={editable}
            />
          ))}
        </div>
      </div>
    );
  }

  const bracketColumns = (
    <div data-bracket-row className="inline-flex min-w-max gap-0 px-2 py-4">
      {rounds.map((round, roundIndex) => (
        <BracketRoundColumn
          key={roundIndex}
          round={round}
          roundIndex={roundIndex}
          totalRounds={totalRounds}
          isLastRound={roundIndex === totalRounds - 1}
          isElimination={isElimination}
          roundLabel={getRoundLabel(roundIndex, totalRounds, format)}
          onMatchClick={onMatchClick}
          editable={editable}
        />
      ))}
    </div>
  );

  return (
    <div className={cn('space-y-4', !embedded && 'animate-in fade-in duration-300')}>
      {!compact && (
        <BracketProgressBar completed={completedMatches} total={totalMatches} />
      )}

      {embedded ? (
        bracketColumns
      ) : (
        <BracketPanCanvas className={panCanvasClassName}>{bracketColumns}</BracketPanCanvas>
      )}
    </div>
  );
};

interface BracketRoundColumnProps {
  round: BracketNode[];
  roundIndex: number;
  totalRounds: number;
  isLastRound: boolean;
  isElimination: boolean;
  roundLabel: string;
  onMatchClick?: (match: TournamentMatch) => void;
  editable?: boolean;
}

const BracketRoundColumn: React.FC<BracketRoundColumnProps> = ({
  round,
  roundIndex,
  isLastRound,
  isElimination,
  roundLabel,
  onMatchClick,
  editable,
}) => {
  const isFinal = roundLabel === 'Final';

  return (
    <div className="flex">
      <div className="flex w-[248px] flex-col">
        <div className="mb-4 flex justify-center">
          <div
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider',
              isFinal
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-border/60 bg-muted/30 text-muted-foreground'
            )}
          >
            {isFinal && <Trophy className="h-3 w-3" />}
            {roundLabel}
          </div>
        </div>

        <div className="relative flex-1">
          {round.map((node, matchIndex) => (
            <div
              key={node.id}
              className="absolute left-0 right-0"
              style={{
                top: isElimination
                  ? getMatchOffset(roundIndex, matchIndex)
                  : matchIndex * (MATCH_SLOT_HEIGHT + 12),
              }}
            >
              <MatchCard
                node={node}
                onClick={
                  editable && onMatchClick ? () => onMatchClick(node.match) : undefined
                }
                editable={editable}
                showConnector={!isLastRound && isElimination}
              />
            </div>
          ))}

          <div
            style={{
              height: isElimination
                ? getRoundColumnHeight(roundIndex, round.length)
                : round.length * (MATCH_SLOT_HEIGHT + 12),
            }}
          />
        </div>
      </div>

      {!isLastRound && isElimination && (
        <div className="w-10 shrink-0">
          <BracketConnectors
            roundIndex={roundIndex}
            matchCount={round.length}
          />
        </div>
      )}
    </div>
  );
};

interface BracketConnectorsProps {
  roundIndex: number;
  matchCount: number;
}

const BracketConnectors: React.FC<BracketConnectorsProps> = ({
  roundIndex,
  matchCount,
}) => {
  const pairs = Math.ceil(matchCount / 2);

  return (
    <div className="relative h-full w-full">
      {Array.from({ length: pairs }).map((_, pairIndex) => {
        const topMatchOffset = getMatchOffset(roundIndex, pairIndex * 2);
        const bottomMatchOffset =
          pairIndex * 2 + 1 < matchCount
            ? getMatchOffset(roundIndex, pairIndex * 2 + 1)
            : topMatchOffset;
        const nextMatchOffset = getMatchOffset(roundIndex + 1, pairIndex);

        const topY = topMatchOffset + MATCH_SLOT_HEIGHT / 2;
        const bottomY = bottomMatchOffset + MATCH_SLOT_HEIGHT / 2;
        const nextY = nextMatchOffset + MATCH_SLOT_HEIGHT / 2;
        const midY = (topY + bottomY) / 2;

        return (
          <svg
            key={pairIndex}
            className="absolute left-0 overflow-visible"
            style={{ top: 0, width: 40, height: getRoundColumnHeight(roundIndex, matchCount) }}
          >
            <path
              d={`M 0 ${topY} H 16 V ${midY} H 32 V ${nextY} H 40`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-border"
            />
            {pairIndex * 2 + 1 < matchCount && (
              <path
                d={`M 0 ${bottomY} H 16 V ${midY}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="text-border"
              />
            )}
          </svg>
        );
      })}
    </div>
  );
};

function getMatchOffset(roundIndex: number, matchIndex: number): number {
  const blockSize = MATCH_SLOT_HEIGHT * Math.pow(2, roundIndex);
  return matchIndex * blockSize * 2 + (blockSize - MATCH_SLOT_HEIGHT) / 2;
}

function getRoundColumnHeight(roundIndex: number, matchCount: number): number {
  if (matchCount === 0) return 0;
  const lastOffset = getMatchOffset(roundIndex, matchCount - 1);
  return lastOffset + MATCH_SLOT_HEIGHT + 16;
}

function getRoundLabel(
  roundIndex: number,
  totalRounds: number,
  format?: TournamentFormat
): string {
  if (format === 'swiss') return `Ronda ${roundIndex + 1}`;
  if (format === 'group_stage') return `Grupo ${String.fromCharCode(65 + roundIndex)}`;
  if (format === 'round_robin') return 'Partidas';

  const fromEnd = totalRounds - roundIndex - 1;
  const names = ['Final', 'Semifinal', 'Cuartos de final', 'Octavos de final', 'Dieciseisavos'];
  if (fromEnd < names.length) return names[fromEnd];
  return `Ronda ${roundIndex + 1}`;
}

interface MatchCardProps {
  node: BracketNode;
  onClick?: () => void;
  editable?: boolean;
  showConnector?: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({
  node,
  onClick,
  editable,
  showConnector,
}) => {
  const { match, team1, team2 } = node;
  const isClickable = editable && onClick;

  return (
    <div className="relative px-1">
      <div
        data-bracket-interactive={isClickable ? true : undefined}
        {...(isClickable ? {
          role: 'button' as const,
          tabIndex: 0,
          onClick,
          onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); },
        } : {})}
        className={cn(
          'overflow-hidden rounded-lg border bg-card shadow-sm transition-all duration-200',
          statusBorderClass(match.status),
          isClickable &&
            'cursor-pointer hover:border-primary/50 hover:shadow-md hover:shadow-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
        )}
      >
        <div className="flex items-center justify-between border-b border-border/40 bg-muted/20 px-2.5 py-1">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            #{match.match_number}
            {match.best_of > 1 && ` · Bo${match.best_of}`}
          </span>
          <MatchStatusBadge status={match.status} />
        </div>

        <TeamRow
          team={team1}
          score={match.team1_score}
          isWinner={!!team1 && match.winner_id === team1.id}
          isLoser={
            match.status === 'completed' && !!team1 && match.winner_id !== team1.id
          }
          showScore={match.status === 'completed' || match.status === 'in_progress'}
        />
        <TeamRow
          team={team2}
          score={match.team2_score}
          isWinner={!!team2 && match.winner_id === team2.id}
          isLoser={
            match.status === 'completed' && !!team2 && match.winner_id !== team2.id
          }
          showScore={match.status === 'completed' || match.status === 'in_progress'}
          isLast
        />

        {match.scheduled_time && match.status !== 'completed' && (
          <div className="flex items-center gap-1.5 border-t border-border/40 bg-muted/10 px-2.5 py-1.5">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">
              {new Date(match.scheduled_time).toLocaleString('es-ES', {
                dateStyle: 'short',
                timeStyle: 'short',
              })}
            </span>
          </div>
        )}
      </div>

      {showConnector && (
        <div className="absolute -right-1 top-1/2 h-px w-2 -translate-y-1/2 bg-border" />
      )}
    </div>
  );
};

interface TeamRowProps {
  team?: TournamentTeam;
  score?: number;
  isWinner: boolean;
  isLoser: boolean;
  showScore: boolean;
  isLast?: boolean;
}

const TeamRow: React.FC<TeamRowProps> = ({
  team,
  score,
  isWinner,
  isLoser,
  showScore,
  isLast,
}) => {
  const displayName = team
    ? team.team_tag
      ? team.team_tag
      : team.team_name
    : 'Por definir';

  const fullName = team
    ? team.team_tag
      ? team.team_name
      : undefined
    : undefined;

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2.5 py-2 transition-colors',
        !isLast && 'border-b border-border/30',
        isWinner && 'bg-primary/10',
        isLoser && 'opacity-50'
      )}
    >
      <TeamAvatar team={team} />
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'truncate text-sm leading-tight',
            isWinner ? 'font-semibold text-foreground' : 'text-foreground/90',
            !team && 'italic text-muted-foreground'
          )}
        >
          {displayName}
        </p>
        {fullName && (
          <p className="truncate text-[10px] text-muted-foreground">{fullName}</p>
        )}
      </div>
      {showScore && score !== undefined && (
        <span
          className={cn(
            'min-w-[1.25rem] text-right text-sm font-bold tabular-nums',
            isWinner ? 'text-primary' : 'text-muted-foreground'
          )}
        >
          {score}
        </span>
      )}
      {isWinner && (
        <div className="h-4 w-0.5 shrink-0 rounded-full bg-primary" />
      )}
    </div>
  );
};

const TeamAvatar: React.FC<{ team?: TournamentTeam }> = ({ team }) => {
  if (team?.logo_url) {
    return (
      <img 
        src={team.logo_url} 
        alt={team.team_name} 
        width={28}
        height={28}
        loading="lazy"
        className="h-7 w-7 shrink-0 rounded-md border border-border/50 object-cover" 
      />
    );
  }

  const initials = team
    ? (team.team_tag || team.team_name).slice(0, 2).toUpperCase()
    : '?';

  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border/50 bg-muted/40 text-[10px] font-bold text-muted-foreground">
      {initials}
    </div>
  );
};

const MATCH_STATUS_VARIANT_MAP: Record<TournamentMatch['status'], { className: string }> = {
  pending: { className: 'bg-muted text-muted-foreground border-border/50' },
  waiting: { className: 'bg-muted/60 text-muted-foreground/70 border-border/40' },
  in_progress: { className: 'bg-primary/15 text-primary border-primary/30' },
  completed: { className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  walkover: { className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  disputed: { className: 'bg-destructive/15 text-destructive border-destructive/30' },
};

const MatchStatusBadge: React.FC<{ status: TournamentMatch['status'] }> = ({ status }) => {
  return (
    <Badge
      variant="outline"
      className={cn('h-4 px-1.5 text-[9px] font-medium', MATCH_STATUS_VARIANT_MAP[status].className)}
    >
      {MATCH_STATUS_LABELS[status]}
    </Badge>
  );
};

function statusBorderClass(status: TournamentMatch['status']): string {
  const map: Record<TournamentMatch['status'], string> = {
    pending: 'border-border/60',
    waiting: 'border-border/40',
    in_progress: 'border-primary/40 ring-1 ring-primary/15',
    completed: 'border-border/60',
    walkover: 'border-amber-500/30',
    disputed: 'border-destructive/40',
  };
  return map[status] ?? 'border-border/60';
}

const BracketProgressBar: React.FC<{ completed: number; total: number }> = ({
  completed,
  total,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/50 bg-muted/20 px-4 py-3">
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Swords className="h-4 w-4 text-primary" />
      <span>
        <span className="font-medium text-foreground">{completed}</span>
        {' / '}
        {total} partidas completadas
      </span>
    </div>
    <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full bg-primary transition-all duration-500"
        style={{ width: total > 0 ? `${(completed / total) * 100}%` : '0%' }}
      />
    </div>
  </div>
);

const BracketEmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border/50 bg-muted/10 px-8 py-16 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted/30">
      <Swords className="h-6 w-6 text-muted-foreground" />
    </div>
    <div>
      <p className="font-medium text-foreground">Sin partidas generadas</p>
      <p className="mt-1 text-sm text-muted-foreground">
        El bracket se mostrará aquí una vez generado
      </p>
    </div>
  </div>
);

export default BracketView;
